import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import type { Article, ModePaiement, Paiement, Vendeur, Vente } from '@troc/shared'
import {
  centimesDepuisSaisie,
  codeFamille,
  decomposerVente,
  eur,
  euroBrut,
  GROUPE_COMMISSION_DEFAUT,
  MOIS_FIN_CONTRAT,
  prixDemarque,
  tauxEnPourcent,
} from '@troc/shared'
import { api, ErreurApi } from '../api'
import { Ecran } from '../composants/Ecran'
import { Pave, appliquerTouche } from '../composants/Pave'
import { ChoixVendeur, restaurerVendeurActif } from '../composants/ChoixVendeur'
import { ApercuTicket } from '../composants/ApercuTicket'
import { useEstVisible } from '../composants/Volets'
import { useScanner } from '../composants/useScanner'
import { useEtroit } from '../composants/useEtroit'
import { NiveauxDemarque } from '../composants/NiveauxDemarque'
import { PanneauDiminutions, baisseDue } from '../composants/PanneauDiminutions'
/**
 * Le décodeur de codes-barres pèse un demi-méga : il n'a rien à faire dans le
 * paquet que la caisse charge au réveil de l'iPad, alors qu'on ne s'en sert
 * qu'en dépannage. Il est donc chargé au premier appui sur le bouton, et
 * conservé ensuite. Le service worker le met en cache comme le reste : une
 * fois la caisse ouverte une fois, il fonctionne hors ligne.
 */
import type { ResultatScan } from '../composants/ScannerCamera'

const ScannerCamera = lazy(() =>
  import('../composants/ScannerCamera').then((m) => ({ default: m.ScannerCamera })),
)
import { useToast } from '../etat/Toast'
import { imprimerTicket } from '../impression/documents'

/**
 * Un seul écran de caisse : la douchette est toujours active, le pavé sert
 * pour un numéro d'article ou un montant libre, et un bouton ouvre la
 * recherche par nom quand l'étiquette manque ou est abîmée. Trois modes à
 * onglets ont existé jusqu'au 29/08/2026 (Comptoir/Grand scan/Chercher
 * l'article) mais se recoupaient trop pour justifier de changer d'écran.
 */
interface LigneTicket {
  cle: number
  articleId: number | null
  ref: string
  libelle: string
  categorie: string
  quantite: number
  /** Exemplaires encore en rayon — plafond du bouton « + ». */
  stock: number
  /** État de démarque de l'article, réglable depuis le ticket. */
  mois: number
  decote: number
  /** Décote due à l'ancienneté — ce que le bouton « Auto » applique. */
  decoteConseillee: number
  decoteManuelle: number | null
  /** « Dévaluation progressive » : à false, le prix de l'article est gelé. */
  demarqueActive: boolean
  prixInitialCentimes: number
  /** Prix unitaire réellement encaissé — modifiable au comptoir. */
  prixCentimes: number
  /** Prix affiché en rayon, conservé pour signaler la négociation. */
  prixReferenceCentimes: number
  taux: number
  netUnitaireCentimes: number
  deposantNom: string | null
}

let compteurLigne = 0

/**
 * Ce qui revient au déposant, et ce que la boutique garde.
 *
 * Le magasin vend au prix de l'étiquette, qui suit mal la démarque — personne
 * ne recolle 80 000 vignettes tous les mois. Le déposant, lui, touche sa part
 * du **prix prévu à son contrat** : c'est ce qu'il a signé. La boutique garde
 * la différence.
 *
 * Le plafond joue dans les deux sens : un geste commercial qui descend sous le
 * prix du contrat ne permet pas de reverser plus que ce qu'on a encaissé.
 *
 * Le calcul est refait à l'identique côté serveur, qui seul fait foi — ici il
 * ne sert qu'à annoncer le juste montant au bas du ticket.
 */
function netDeposant(
  prixPratiqueCentimes: number,
  article: Pick<
    Article,
    'prixInitialCentimes' | 'mois' | 'demarqueActive' | 'tauxCommission' | 'deposantGroupeCommission'
  >,
): number {
  const prixContrat = article.demarqueActive
    ? prixDemarque(article.prixInitialCentimes, article.mois)
    : article.prixInitialCentimes
  return decomposerVente(
    Math.min(prixPratiqueCentimes, prixContrat),
    article.tauxCommission,
    article.deposantGroupeCommission,
  ).netDeposantCentimes
}

export function Caisse() {
  const { signaler, alerter } = useToast()
  const [camera, setCamera] = useState(false)
  /**
   * Sur iPhone, le pavé et le ticket ne tiennent pas côte à côte : on montre
   * l'un ou l'autre. La saisie d'abord — c'est là qu'on encode — et le ticket
   * sur demande.
   *
   * Pas de bascule automatique à chaque article ajouté : on encode les pièces
   * l'une après l'autre, et sauter au ticket entre deux lectures ferait perdre
   * le champ de saisie. Le retour visuel est porté par le bouton lui-même, qui
   * annonce le nombre d'articles et le total.
   */
  const etroit = useEtroit()
  const [vueEtroite, setVueEtroite] = useState<'saisie' | 'ticket'>('saisie')
  // Liste ou aperçu du ticket imprimé — deux lectures du même contenu.
  const [apercu, setApercu] = useState(false)
  // Chercher une pièce par son nom au lieu de la scanner — pour une étiquette
  // absente ou abîmée. Un simple panneau, pas un écran de caisse à part : le
  // ticket et le pavé restent sous les yeux, on y revient d'un bouton.
  const [rechercheOuverte, setRechercheOuverte] = useState(false)
  const [lignes, setLignes] = useState<LigneTicket[]>([])

  /** La fenêtre des baisses dues, ouverte depuis le pied du ticket. */
  const [diminutionsOuvertes, setDiminutionsOuvertes] = useState(false)
  /**
   * Le ticket tel qu'il est à l'instant, lisible sans redéclarer l'ajout à
   * chaque frappe : on a besoin de savoir si la pièce y figure déjà avant de
   * décider s'il faut poser la question.
   */
  const lignesRef = useRef<LigneTicket[]>([])
  useEffect(() => {
    lignesRef.current = lignes
  }, [lignes])
  const [saisie, setSaisie] = useState('')
  const [derniereLecture, setDerniereLecture] = useState('')
  const [paiementOuvert, setPaiementOuvert] = useState(false)
  // Quelle tuile de paiement a été touchée pour ouvrir l'encaissement — le
  // panneau s'ouvre déjà sur ce mode plutôt que sur « Espèces » par défaut.
  const [modeChoisi, setModeChoisi] = useState<ModePaiement>('especes')
  const [venteFinalisee, setVenteFinalisee] = useState<Vente | null>(null)

  const total = lignes.reduce((somme, ligne) => somme + ligne.prixCentimes * ligne.quantite, 0)
  /** Les pièces du ticket qui ont droit à une baisse encore non accordée. */
  const nbBaisses = lignes.filter(baisseDue).length
  const reverse = lignes.reduce(
    (somme, ligne) => somme + ligne.netUnitaireCentimes * ligne.quantite,
    0,
  )

  /**
   * L'article rejoint le ticket **au prix de l'étiquette**, sans question.
   *
   * Une fenêtre s'ouvrait autrefois à chaque pièce dont le contrat prévoyait
   * une baisse. Elle coupait le geste au mauvais moment : on encode à la file,
   * souvent une dizaine de pièces, et le client n'a encore rien demandé. La
   * question de la baisse se pose une fois, à la fin, sur le ticket complet —
   * c'est le bouton « Diminutions » du pied.
   */
  const ajouterArticle = useCallback(
    (article: Article, prixRetenuCentimes?: number) => {
      const prixUnitaire = prixRetenuCentimes ?? article.prixActuelCentimes
      setLignes((actuelles) => {
        // Re-scanner une pièce déjà au ticket incrémente la quantité : c'est le
        // geste naturel quand on encaisse deux chaises identiques.
        const existante = actuelles.find((l) => l.articleId === article.id)
        if (existante) {
          if (existante.quantite >= existante.stock) {
            alerter(
              `${article.ref} — ${existante.stock} exemplaire${existante.stock > 1 ? 's' : ''} en rayon seulement.`,
            )
            return actuelles
          }
          return actuelles.map((l) =>
            l.cle === existante.cle ? { ...l, quantite: l.quantite + 1 } : l,
          )
        }
        const part = decomposerVente(prixUnitaire, article.tauxCommission, article.deposantGroupeCommission)
        compteurLigne += 1
        const net = netDeposant(prixUnitaire, article)
        return [
          ...actuelles,
          {
            cle: compteurLigne,
            articleId: article.id,
            ref: article.ref,
            libelle: article.titre,
            categorie: article.categorie,
            quantite: 1,
            stock: article.quantiteRestante,
            mois: article.mois,
            decote: article.decote,
            decoteConseillee: article.decoteConseillee,
            decoteManuelle: article.decoteManuelle,
            demarqueActive: article.demarqueActive,
            prixInitialCentimes: article.prixInitialCentimes,
            prixCentimes: part.prixCentimes,
            prixReferenceCentimes: article.prixActuelCentimes,
            taux: part.taux,
            netUnitaireCentimes: net,
            deposantNom: article.deposantNom,
          },
        ]
      })
      setDerniereLecture(`${article.ref} — ${article.titre} · ${eur(prixUnitaire)}`)
      setSaisie('')
    },
    [alerter],
  )

  const ajouterParReference = useCallback(
    async (reference: string) => {
      const propre = reference.trim()
      if (propre.length < 3) {
        alerter('Référence trop courte.')
        return
      }
      try {
        const reponse = await api<{ article: Article }>(
          `/articles/ref/${encodeURIComponent(propre)}`,
        )
        ajouterArticle(reponse.article)
      } catch (cause) {
        alerter(cause instanceof ErreurApi ? cause.message : 'Lecture impossible.')
      }
    },
    [ajouterArticle, alerter],
  )

  /**
   * Résolution d'un code lu par la caméra.
   *
   * Contrairement au reste de la caisse, on ne passe pas par les bulles
   * d'alerte : elles s'affichent derrière la fenêtre du scanner, donc
   * personne ne les voit. Le résultat est rendu au scanner, qui l'inscrit
   * dans son panier — refus compris, avec sa raison.
   */
  const resoudreScan = useCallback(
    async (code: string): Promise<ResultatScan> => {
      const propre = code.trim()
      if (propre.length < 3) return { ok: false, libelle: propre || 'code illisible' }
      try {
        const reponse = await api<{ article: Article }>(
          `/articles/ref/${encodeURIComponent(propre)}`,
        )
        ajouterArticle(reponse.article)
        return {
          ok: true,
          libelle: reponse.article.titre,
          detail: eur(reponse.article.prixActuelCentimes),
        }
      } catch (cause) {
        return {
          ok: false,
          libelle: propre,
          detail: cause instanceof ErreurApi ? cause.message : 'Lecture impossible.',
        }
      }
    },
    [ajouterArticle],
  )

  /**
   * Résolution d'une photo d'étiquette sans code-barres, prise depuis la même
   * caméra que le scanner en continu — voir la note dans ScannerCamera.tsx.
   */
  const resoudrePhoto = useCallback(
    async (image: string): Promise<ResultatScan> => {
      try {
        const reponse = await api<{ article: Article; lu: string }>('/lecture/etiquette', {
          methode: 'POST',
          corps: { image },
        })
        ajouterArticle(reponse.article)
        return { ok: true, libelle: reponse.article.titre, detail: reponse.lu }
      } catch (cause) {
        return {
          ok: false,
          libelle: 'photo',
          detail: cause instanceof ErreurApi ? cause.message : 'Lecture impossible.',
        }
      }
    },
    [ajouterArticle],
  )

  // La douchette est écoutée en permanence, sauf pendant l'encaissement où les
  // chiffres doivent aller au pavé espèces.
  useScanner((code) => void ajouterParReference(code), !paiementOuvert)

  // « Encaisser » depuis la base articles dépose la référence ici au passage.
  const emplacement = useLocation()
  const naviguer = useNavigate()
  const refAAjouter = (emplacement.state as { ajouterRef?: string } | null)?.ajouterRef
  const caisseVisible = useEstVisible('/caisse')
  useEffect(() => {
    // Même précaution qu'aux contrats : l'écran reste monté en coulisse, et
    // une redirection partie de là détournerait la navigation.
    if (!refAAjouter || !caisseVisible) return
    naviguer('/caisse', { replace: true, state: null })
    void ajouterParReference(refAAjouter)
  }, [refAAjouter, caisseVisible, ajouterParReference, naviguer])

  const retirerLigne = (cle: number) =>
    setLignes((actuelles) => actuelles.filter((l) => l.cle !== cle))

  /**
   * Prix négocié au comptoir. Ce que touche le déposant ne bouge pas quand on
   * vend au-dessus du prix de son contrat — c'est la boutique qui encaisse
   * l'écart. En revanche, un geste qui descend sous ce prix le fait baisser :
   * on ne reverse jamais plus qu'on n'a reçu.
   */
  const changerPrix = (cle: number, centimes: number) =>
    setLignes((actuelles) =>
      actuelles.map((ligne) => {
        if (ligne.cle !== cle) return ligne
        if (ligne.articleId === null) {
          return { ...ligne, prixCentimes: centimes, netUnitaireCentimes: 0 }
        }
        return {
          ...ligne,
          prixCentimes: centimes,
          netUnitaireCentimes: netDeposant(centimes, {
            prixInitialCentimes: ligne.prixInitialCentimes,
            mois: ligne.mois,
            demarqueActive: ligne.demarqueActive,
            tauxCommission: ligne.taux,
            // Le taux est déjà figé sur la ligne : le groupe ne sert plus.
            deposantGroupeCommission: GROUPE_COMMISSION_DEFAUT,
          }),
        }
      }),
    )

  /** Réaligne une ligne du ticket sur l'article après un réglage de démarque. */
  const rafraichirDepuisArticle = (cle: number, article: Article) =>
    setLignes((actuelles) =>
      actuelles.map((ligne) => {
        if (ligne.cle !== cle) return ligne
        const part = decomposerVente(
          article.prixActuelCentimes,
          article.tauxCommission,
          article.deposantGroupeCommission,
        )
        return {
          ...ligne,
          mois: article.mois,
          decote: article.decote,
          decoteConseillee: article.decoteConseillee,
          decoteManuelle: article.decoteManuelle,
          demarqueActive: article.demarqueActive,
          prixInitialCentimes: article.prixInitialCentimes,
          prixCentimes: part.prixCentimes,
          prixReferenceCentimes: article.prixActuelCentimes,
          netUnitaireCentimes: netDeposant(article.prixActuelCentimes, article),
        }
      }),
    )

  /** Une quantité tombée à zéro retire la ligne — un geste de moins. */
  const changerQuantite = (cle: number, delta: number) =>
    setLignes((actuelles) =>
      actuelles.flatMap((ligne) => {
        if (ligne.cle !== cle) return [ligne]
        const quantite = ligne.quantite + delta
        if (quantite < 1) return []
        if (ligne.articleId !== null && quantite > ligne.stock) {
          alerter(
            `${ligne.ref} — ${ligne.stock} exemplaire${ligne.stock > 1 ? 's' : ''} en rayon seulement.`,
          )
          return [ligne]
        }
        return [{ ...ligne, quantite }]
      }),
    )

  const ajouterMontantLibre = () => {
    const centimes = centimesDepuisSaisie(saisie)
    if (!centimes || centimes <= 0) {
      alerter('Saisissez d’abord un montant sur le pavé.')
      return
    }
    compteurLigne += 1
    setLignes((actuelles) => [
      ...actuelles,
      {
        cle: compteurLigne,
        articleId: null,
        ref: 'LIBRE',
        libelle: 'Vente au montant libre',
        categorie: 'Divers',
        quantite: 1,
        stock: 999,
        mois: 0,
        decote: 0,
        decoteConseillee: 0,
        decoteManuelle: null,
        demarqueActive: true,
        prixInitialCentimes: centimes,
        prixCentimes: centimes,
        prixReferenceCentimes: centimes,
        taux: 0,
        netUnitaireCentimes: 0,
        deposantNom: null,
      },
    ])
    setSaisie('')
  }

  const nouveauTicket = () => {
    setLignes([])
    setSaisie('')
    setDerniereLecture('')
    setPaiementOuvert(false)
    setVenteFinalisee(null)
  }

  const surVenteEnregistree = (vente: Vente) => {
    setVenteFinalisee(vente)
    imprimerTicket(vente)
  }

  return (
    <Ecran titre="Caisse" sousTitre={lignes.length > 0 ? 'Vente en cours' : 'Prêt à encaisser'}>
      {camera && (
        <Suspense fallback={<div className="voile" />}>
          <ScannerCamera
            onCode={resoudreScan}
            onPhoto={resoudrePhoto}
            onFermer={() => setCamera(false)}
          />
        </Suspense>
      )}

      <div className={etroit ? `caisse etroite vue-${vueEtroite}` : 'caisse'}>
        <div className="caisse-ticket" style={{ flex: 1, minWidth: 0 }}>
          <div className="ticket-entete">
            <span className="titre" style={{ fontSize: 16, letterSpacing: '0.02em' }}>
              TICKET EN COURS
            </span>
            {/* Deux lectures du même ticket : la liste sert à agir — corriger
                un prix, changer une quantité — l'aperçu montre ce que le client
                recevra. Les erreurs de mise en page ne se voient que là. */}
            <div className="seg">
              <button
                type="button"
                className="seg-opt"
                aria-pressed={!apercu}
                onClick={() => setApercu(false)}
              >
                Liste
              </button>
              <button
                type="button"
                className="seg-opt"
                aria-pressed={apercu}
                onClick={() => setApercu(true)}
              >
                Aperçu
              </button>
            </div>
          </div>

          {apercu ? (
            <div className="ticket-lignes">
              <ApercuTicket
                lignes={lignes.map((l) => ({
                  ref: l.ref,
                  libelle: l.libelle,
                  quantite: l.quantite,
                  prixCentimes: l.prixCentimes,
                }))}
                total={total}
              />
            </div>
          ) : (
          <div className="ticket-lignes">
            {lignes.length === 0 && (
              <div className="vide">
                Aucun article.
                <br />
                Scannez une étiquette pour commencer.
              </div>
            )}
            {/* Deux rangées par article : ce qu'on lit en haut, ce sur quoi
                on agit en bas. La colonne du ticket est étroite dans deux modes
                sur trois — une seule rangée y écraserait la désignation, et les
                paliers de prix s'y empileraient en colonne. */}
            {lignes.map((ligne) => (
              <div key={ligne.cle} className="ticket-ligne nouvelle">
                <div className="ligne-haut">
                  <div className="ligne-nom">
                    <div className="titre">{ligne.libelle}</div>
                    <div className="sourdine ligne-meta">
                      <span className="ligne-ref">{ligne.ref}</span>
                      {ligne.categorie && ` · ${codeFamille(ligne.categorie)}`}
                      {ligne.articleId !== null && ` · comm. ${tauxEnPourcent(ligne.taux)}`}
                      {ligne.quantite > 1 && ` · ${eur(ligne.prixCentimes)} l’unité`}
                    </div>
                  </div>
                  <PrixModifiable
                    ligne={ligne}
                    onPrix={(centimes) => changerPrix(ligne.cle, centimes)}
                    onErreur={alerter}
                  />
                  <button
                    type="button"
                    className="btn btn-secondaire btn-icone"
                    onClick={() => retirerLigne(ligne.cle)}
                    aria-label={`Retirer ${ligne.libelle}`}
                  >
                    ×
                  </button>
                </div>

                <div className="ligne-bas">
                  <div className="compteur-quantite">
                    <button
                      type="button"
                      onClick={() => changerQuantite(ligne.cle, -1)}
                      aria-label={`Retirer un exemplaire de ${ligne.libelle}`}
                    >
                      −
                    </button>
                    <span className="chiffre">{ligne.quantite}</span>
                    <button
                      type="button"
                      onClick={() => changerQuantite(ligne.cle, 1)}
                      disabled={ligne.articleId !== null && ligne.quantite >= ligne.stock}
                      aria-label={`Ajouter un exemplaire de ${ligne.libelle}`}
                    >
                      +
                    </button>
                  </div>

                  {ligne.articleId !== null && (
                    <NiveauxDemarque
                      article={{
                        id: ligne.articleId,
                        decote: ligne.decote,
                        decoteConseillee: ligne.decoteConseillee,
                        decoteManuelle: ligne.decoteManuelle,
                        demarqueActive: ligne.demarqueActive,
                        prixInitialCentimes: ligne.prixInitialCentimes,
                      }}
                      onArticle={(article) => rafraichirDepuisArticle(ligne.cle, article)}
                      onErreur={alerter}
                      variante="liste"
                    />
                  )}

                  {ligne.prixCentimes !== ligne.prixReferenceCentimes && (
                    <span className="marqueur-negocie">
                      négocié · rayon {eur(ligne.prixReferenceCentimes)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
          )}

          <div className="ticket-total">
            <div className="ligne">
              <span>Sous-total</span>
              <span>{eur(total)}</span>
            </div>
            <div className="ligne">
              <span>Dont reversé aux déposants</span>
              <span>{eur(reverse)}</span>
            </div>
            <div className="a-payer">
              <span className="titre" style={{ fontSize: 16 }}>
                À PAYER
              </span>
              <span className="chiffre montant">{eur(total)}</span>
            </div>
            {/* La question de la baisse se pose ici, une fois, sur le ticket
                complet — et non à chaque pièce encodée. */}
            {nbBaisses > 0 && (
              <button
                type="button"
                className="btn btn-secondaire btn-bloc btn-centre"
                style={{ marginBottom: 8 }}
                onClick={() => setDiminutionsOuvertes(true)}
              >
                Diminutions · {nbBaisses} pièce{nbBaisses > 1 ? 's' : ''} a{nbBaisses > 1 ? 'ont' : ''} baissé
              </button>
            )}
            {/* Les modes de paiement restent visibles en permanence, comme au
                comptoir un client annonce déjà « en espèces » avant même
                qu'on lui dise le total : pas besoin d'un aller-retour dans un
                panneau à part pour choisir. La touche ouvre le même circuit
                de validation qu'avant — vendeur confirmé, rendu calculé — ce
                n'est que le point d'entrée qui change, pas la sécurité. */}
            <div className="tuiles-paiement">
              {MODES.map((m) => (
                <button
                  key={m.cle}
                  type="button"
                  className={`tuile-paiement tuile-paiement-${m.cle}`}
                  disabled={lignes.length === 0}
                  onClick={() => {
                    setModeChoisi(m.cle)
                    setPaiementOuvert(true)
                  }}
                >
                  <span>{m.libelle}</span>
                  <span aria-hidden="true">→</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="caisse-pave">
          {rechercheOuverte ? (
            <>
              <div style={{ padding: '12px 18px', borderBottom: '2px solid var(--color-divider)' }}>
                <button
                  type="button"
                  className="btn btn-secondaire btn-centre"
                  onClick={() => setRechercheOuverte(false)}
                >
                  ← Retour au pavé
                </button>
              </div>
              <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '14px 18px' }}>
                <RechercheVisuelle
                  onChoisir={(article) => {
                    ajouterArticle(article)
                    setRechercheOuverte(false)
                  }}
                />
              </div>
            </>
          ) : (
            <>
              <div style={{ padding: '16px 18px', borderBottom: '2px solid var(--color-divider)' }}>
                <button
                  type="button"
                  className="tuile-scan"
                  onClick={() => setCamera(true)}
                >
                  <span className="tuile-scan-icone" aria-hidden="true">▣</span>
                  <span>
                    <span className="tuile-scan-titre">Scanner ou photographier une étiquette</span>
                    <span className="tuile-scan-sous">
                      {derniereLecture ? `Dernière lecture : ${derniereLecture}` : 'Ouvre la caméra'}
                    </span>
                  </span>
                </button>
              </div>
              <div style={{ padding: '14px 18px', borderBottom: '2px solid var(--color-divider)' }}>
                <label className="etiquette" htmlFor="caisse-saisie" style={{ marginBottom: 6, display: 'block' }}>
                  N° d’article ou montant
                </label>
                {/* Un vrai champ, et non un afficheur : au comptoir on tape le
                    numéro au pavé tactile, mais sur un poste à clavier on le
                    frappe directement et on valide d'un Entrée. Les deux
                    alimentent la même saisie. */}
                <input
                  id="caisse-saisie"
                  className="afficheur afficheur-saisie"
                  value={saisie}
                  placeholder="—"
                  inputMode="numeric"
                  autoComplete="off"
                  // Le clavier virtuel de l'iPad ne doit pas s'ouvrir tout seul —
                  // il mangerait la moitié de l'écran alors qu'on scanne. Sur un
                  // poste à clavier, en revanche, le curseur est déjà là.
                  autoFocus={!('ontouchstart' in window)}
                  onChange={(e) => setSaisie(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void ajouterParReference(saisie)
                    if (e.key === 'Escape') setSaisie('')
                  }}
                />
              </div>
              <div
                style={{
                  flex: 1,
                  padding: '14px 18px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                  minHeight: 0,
                }}
              >
                <Pave
                  etire
                  onTouche={(touche) => setSaisie((s) => appliquerTouche(s, touche))}
                  onEffacer={() => setSaisie('')}
                />
              </div>
              <div
                style={{ padding: '0 18px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}
              >
                {/* Le même champ sert au numéro d'article et au montant : les
                    deux boutons disent lequel des deux on vient de taper. */}
                <button
                  type="button"
                  className="btn btn-secondaire btn-centre"
                  onClick={() => void ajouterParReference(saisie)}
                >
                  C’est un n° d’article
                </button>
                <button
                  type="button"
                  className="btn btn-secondaire btn-centre"
                  onClick={ajouterMontantLibre}
                >
                  C’est un montant — sans article
                </button>
                <button
                  type="button"
                  className="btn btn-fantome btn-centre"
                  onClick={() => setRechercheOuverte(true)}
                >
                  Chercher l’article par son nom
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* La bascule ne s'affiche que là où la place manque : sur l'iPad du
          comptoir, les deux colonnes restent visibles ensemble. */}
      {etroit && (
        <div className="bascule-caisse">
          <button
            type="button"
            className="seg-opt"
            aria-pressed={vueEtroite === 'saisie'}
            onClick={() => setVueEtroite('saisie')}
          >
            Encoder
          </button>
          <button
            type="button"
            className="seg-opt"
            aria-pressed={vueEtroite === 'ticket'}
            onClick={() => setVueEtroite('ticket')}
          >
            Ticket
            {lignes.length > 0 && (
              <span className="bascule-compte">
                {lignes.length} · {eur(total)}
              </span>
            )}
          </button>
        </div>
      )}

      {diminutionsOuvertes && (
        <PanneauDiminutions
          lignes={lignes}
          onPrix={changerPrix}
          onFermer={() => setDiminutionsOuvertes(false)}
        />
      )}

      {paiementOuvert && (
        <PanneauPaiement
          lignes={lignes}
          total={total}
          vente={venteFinalisee}
          modeInitial={modeChoisi}
          onFermer={() => setPaiementOuvert(false)}
          onEnregistree={surVenteEnregistree}
          onNouveauTicket={nouveauTicket}
          onErreur={alerter}
          onInfo={signaler}
        />
      )}
    </Ecran>
  )
}

/**
 * Le prix d'une ligne, modifiable d'un appui. On négocie au comptoir : le
 * vendeur touche le montant, tape le prix convenu, et valide.
 */
function PrixModifiable({
  ligne,
  onPrix,
  onErreur,
}: {
  ligne: LigneTicket
  onPrix: (centimes: number) => void
  onErreur: (message: string) => void
}) {
  const [edition, setEdition] = useState<string | null>(null)

  if (edition === null) {
    return (
      <button
        type="button"
        className="prix-ligne"
        onClick={() => setEdition(euroBrut(ligne.prixCentimes))}
        title="Modifier le prix"
      >
        <span className="chiffre">{eur(ligne.prixCentimes * ligne.quantite)}</span>
        {ligne.prixCentimes !== ligne.prixReferenceCentimes && <span className="marqueur">✎</span>}
      </button>
    )
  }

  const valider = () => {
    const centimes = centimesDepuisSaisie(edition)
    if (centimes === null || centimes <= 0) {
      onErreur('Prix invalide.')
      return
    }
    onPrix(centimes)
    setEdition(null)
  }

  return (
    <input
      className="saisie prix-saisie"
      value={edition}
      autoFocus
      inputMode="decimal"
      onChange={(e) => setEdition(e.target.value)}
      onBlur={valider}
      onKeyDown={(e) => {
        if (e.key === 'Enter') valider()
        if (e.key === 'Escape') setEdition(null)
      }}
      aria-label={`Prix de ${ligne.libelle}`}
    />
  )
}

// ── Recherche visuelle ──────────────────────────────────────────────────────

function RechercheVisuelle({ onChoisir }: { onChoisir: (article: Article) => void }) {
  const [requete, setRequete] = useState('')
  const [articles, setArticles] = useState<Article[]>([])
  const [chargement, setChargement] = useState(true)

  useEffect(() => {
    const controleur = new AbortController()
    const minuterie = window.setTimeout(() => {
      setChargement(true)
      api<{ articles: Article[] }>(
        `/articles?statut=en_rayon&limite=60${requete ? `&q=${encodeURIComponent(requete)}` : ''}`,
        { signal: controleur.signal },
      )
        .then((r) => setArticles(r.articles))
        .catch(() => undefined)
        .finally(() => setChargement(false))
    }, 220)
    return () => {
      window.clearTimeout(minuterie)
      controleur.abort()
    }
  }, [requete])

  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRight: '2px solid var(--color-divider)',
      }}
    >
      <div
        style={{
          padding: '16px 20px',
          borderBottom: '2px solid var(--color-divider)',
          display: 'flex',
          gap: 10,
        }}
      >
        <input
          className="saisie saisie-grande"
          placeholder="Rechercher un article, un n°, une catégorie…"
          value={requete}
          onChange={(e) => setRequete(e.target.value)}
        />
      </div>
      <div className="grille-articles">
        {articles.map((article) => (
          <button
            key={article.id}
            type="button"
            className="vignette"
            onClick={() => onChoisir(article)}
          >
            <div className="haut">
              <span className="ref">{article.ref}</span>
              <span className="sourdine" style={{ fontSize: 10 }}>
                {article.categorie}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8, flex: 1 }}>
              <span className="nom">{article.titre}</span>
            </div>
            <div className="bas">
              <span className="chiffre" style={{ fontSize: 19 }}>
                {eur(article.prixActuelCentimes)}
              </span>
              <span className="sourdine" style={{ fontSize: 10 }}>
                {article.decoteConseillee > 0
                  ? `démarque due −${Math.round(article.decoteConseillee * 100)} %`
                  : ''}
              </span>
            </div>
          </button>
        ))}
        {articles.length === 0 && (
          <div className="vide" style={{ gridColumn: '1 / -1' }}>
            {chargement ? 'Recherche…' : 'Aucun article ne correspond.'}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Encaissement ────────────────────────────────────────────────────────────

const MODES: { cle: ModePaiement; libelle: string }[] = [
  { cle: 'especes', libelle: 'Espèces' },
  { cle: 'cb', libelle: 'Carte bancaire' },
  { cle: 'mixte', libelle: 'Paiement mixte' },
]

function PanneauPaiement({
  lignes,
  total,
  vente,
  modeInitial,
  onFermer,
  onEnregistree,
  onNouveauTicket,
  onErreur,
  onInfo,
}: {
  lignes: LigneTicket[]
  total: number
  vente: Vente | null
  modeInitial: ModePaiement
  onFermer: () => void
  onEnregistree: (vente: Vente) => void
  onNouveauTicket: () => void
  onErreur: (message: string) => void
  onInfo: (message: string) => void
}) {
  const [mode, setMode] = useState<ModePaiement>(modeInitial)
  const [saisie, setSaisie] = useState('')
  const [envoi, setEnvoi] = useState(false)
  // Qui encaisse. Le bouton de validation reste inerte tant que ce n'est
  // pas répondu : une vente sans auteur ne se rattrape pas après coup.
  const [vendeur, setVendeur] = useState<Vendeur | null>(null)

  const montantSaisi = centimesDepuisSaisie(saisie) ?? 0

  // Arrondis usuels au-dessus du total : ce que le client tend le plus souvent.
  const raccourcis = useMemo(() => {
    const propositions = [total, Math.ceil(total / 500) * 500, Math.ceil(total / 1000) * 1000,
      Math.ceil(total / 5000) * 5000, Math.ceil(total / 10000) * 10000]
    return [...new Set(propositions)].filter((v) => v >= total).slice(0, 4)
  }, [total])

  const { paiements, resteLibelle, resteCentimes, valide, aide } = useMemo(() => {
    if (mode === 'cb') {
      return {
        paiements: [{ mode: 'cb' as const, montantCentimes: total }],
        resteLibelle: 'Envoyé au terminal',
        resteCentimes: total,
        valide: true,
        aide: 'Transaction transmise au TPE. Le ticket s’imprime après validation.',
      }
    }
    if (mode === 'mixte') {
      const partEspeces = Math.min(montantSaisi, total)
      return {
        paiements: [
          { mode: 'especes' as const, montantCentimes: partEspeces },
          { mode: 'cb' as const, montantCentimes: total - partEspeces },
        ].filter((p) => p.montantCentimes > 0),
        resteLibelle: 'Reste sur carte',
        resteCentimes: total - partEspeces,
        valide: partEspeces > 0 && partEspeces < total,
        aide: 'Le solde part sur le TPE après validation de la part espèces.',
      }
    }
    return {
      paiements: [{ mode: 'especes' as const, montantCentimes: montantSaisi }],
      resteLibelle: montantSaisi >= total ? 'À rendre' : 'Reste dû',
      resteCentimes: Math.abs(montantSaisi - total),
      valide: montantSaisi >= total,
      aide: 'Le tiroir-caisse s’ouvre à la validation.',
    }
  }, [mode, montantSaisi, total])

  const valider = async () => {
    if (!valide || envoi) return
    setEnvoi(true)
    try {
      const reponse = await api<{ vente: Vente }>('/ventes', {
        methode: 'POST',
        corps: {
          mode,
          paiements: paiements as Paiement[],
          lignes: lignes.map((l) => ({
            articleId: l.articleId,
            libelle: l.libelle,
            quantite: l.quantite,
            // Le prix n'est transmis que s'il a été négocié : sinon le serveur
            // recalcule lui-même la démarque du jour.
            prixCentimes:
              l.articleId === null || l.prixCentimes !== l.prixReferenceCentimes
                ? l.prixCentimes
                : undefined,
          })),
        },
      })
      onEnregistree(reponse.vente)
    } catch (cause) {
      onErreur(cause instanceof ErreurApi ? cause.message : 'Encaissement impossible.')
    } finally {
      setEnvoi(false)
    }
  }

  if (vente) {
    return (
      <div className="voile">
        <div className="panneau">
          <div className="panneau-entete">
            <div>
              <div className="etiquette">Ticket n° {vente.ticketNo}</div>
              <div className="chiffre" style={{ fontSize: 30 }}>
                {eur(vente.totalCentimes)}
              </div>
            </div>
          </div>
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 14,
              padding: 40,
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                background: 'var(--color-accent)',
                color: 'var(--color-bg)',
                display: 'grid',
                placeItems: 'center',
                fontSize: 28,
              }}
            >
              ✓
            </div>
            <h3 style={{ margin: 0, fontSize: 30 }}>Vente enregistrée</h3>
            <div style={{ fontSize: 14, color: 'var(--color-neutral-700)', maxWidth: 440 }}>
              {eur(vente.totalCentimes)} encaissés · {vente.lignes.length} article
              {vente.lignes.length > 1 ? 's' : ''} · {eur(vente.reverseCentimes)} portés au crédit
              des déposants.
              {vente.rendueCentimes > 0 && ` Rendu : ${eur(vente.rendueCentimes)}.`}
            </div>
            <div className="note">
              Ticket imprimé · articles sortis du stock · reversements mis à jour.
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button
                type="button"
                className="btn btn-primaire btn-large"
                onClick={onNouveauTicket}
              >
                Nouveau ticket
              </button>
              <button
                type="button"
                className="btn btn-secondaire btn-large"
                onClick={() => {
                  imprimerTicket(vente)
                  onInfo('Ticket renvoyé à l’imprimante.')
                }}
              >
                Réimprimer
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="voile">
      <div className="panneau">
        <div className="panneau-entete">
          <div>
            <div className="etiquette">Encaissement · {lignes.length} article(s)</div>
            <div className="chiffre" style={{ fontSize: 30 }}>
              {eur(total)}
            </div>
          </div>
          <button
            type="button"
            className="btn btn-secondaire btn-icone"
            onClick={onFermer}
            aria-label="Fermer"
          >
            ×
          </button>
        </div>

        <div style={{ display: 'flex', borderBottom: '2px solid var(--color-divider)' }}>
          {MODES.map((m) => (
            <button
              key={m.cle}
              type="button"
              className="seg-opt"
              style={{ flex: 1, padding: '16px 8px', fontSize: 14 }}
              aria-pressed={mode === m.cle}
              onClick={() => {
                setMode(m.cle)
                setSaisie('')
              }}
            >
              {m.libelle}
            </button>
          ))}
        </div>

        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          <div
            style={{
              flex: 1,
              padding: '20px 22px',
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
              borderRight: '1px solid var(--color-divider)',
            }}
          >
            <div className="etiquette">
              {mode === 'cb'
                ? 'Montant envoyé au TPE'
                : mode === 'mixte'
                  ? 'Part réglée en espèces'
                  : 'Montant remis par le client'}
            </div>
            <div className="afficheur afficheur-grand">
              {mode === 'cb' ? eur(total) : saisie ? `${saisie} €` : '0 €'}
            </div>

            {mode !== 'cb' && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {raccourcis.map((valeur) => (
                  <button
                    key={valeur}
                    type="button"
                    className="btn btn-secondaire"
                    onClick={() => setSaisie(String(valeur / 100).replace('.', ','))}
                  >
                    {eur(valeur)}
                  </button>
                ))}
              </div>
            )}

            <div
              style={{
                marginTop: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                <span>{resteLibelle}</span>
                <span className="chiffre" style={{ fontSize: 24 }}>
                  {eur(resteCentimes)}
                </span>
              </div>
              <div className="sourdine" style={{ fontSize: 12 }}>
                {aide}
              </div>
            </div>
          </div>

          {mode !== 'cb' && (
            <div style={{ width: 260, flex: 'none', padding: '20px 22px' }}>
              <Pave
                onTouche={(touche) => setSaisie((s) => appliquerTouche(s, touche))}
                onEffacer={() => setSaisie('')}
              />
            </div>
          )}
        </div>

        {/* Le choix de la personne fait partie du paiement, pas d'une étape
            de plus : il est dans le même pied, juste avant le bouton. */}
        <div className="pied-encaissement">
          <ChoixVendeur titre="Qui encaisse ?" compact onChoisi={setVendeur} />
          <div className="panneau-pied" style={{ borderTop: 0 }}>
            <button type="button" className="btn btn-secondaire btn-large" onClick={onFermer}>
              Annuler
            </button>
            <button
              type="button"
              className="btn btn-primaire btn-large"
              style={{ flex: 1 }}
              disabled={!valide || envoi || !vendeur}
              onClick={() => void valider()}
            >
              {envoi
                ? 'Enregistrement…'
                : !vendeur
                  ? 'Choisissez qui encaisse'
                  : mode === 'cb'
                    ? `Envoyer au TPE ${eur(total)}`
                    : `Valider — ${vendeur.nom}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
